import React, { createRef, useEffect, useImperativeHandle } from 'react'

import {
  Card,
  type CardData,
  type CardLabels,
  type CardProps,
  type CardTimeFormat,
} from '@/components/molecules/Card/Card.tsx'
import { ConfirmDialog, type ConfirmDialogLabels } from '@/components/molecules/ConfirmDialog/ConfirmDialog.tsx'
import {
  ConfirmTeamDialog,
  type ConfirmTeamDialogAction,
  type ConfirmTeamDialogData,
  type ConfirmTeamDialogLabels,
  type ConfirmTeamDialogOptions,
} from '@/components/molecules/ConfirmTeamDialog/ConfirmTeamDialog.tsx'

export type CardAllocatedTimeFormat = CardTimeFormat

export type CardAllocatedOptions = ConfirmTeamDialogOptions

export interface CardAllocatedLabels extends CardLabels {
  cancelConfirmDialog: ConfirmTeamDialogLabels
  putOnHoldConfirmDialog: ConfirmTeamDialogLabels
  customerNotFoundConfirmDialog: ConfirmTeamDialogLabels
  customerTakenCareOfConfirmDialog: ConfirmDialogLabels
}

export interface CardAllocateState extends Pick<CardProps, 'state' | 'size'> {
  highlighted?: boolean
}

export interface CardAllocatedData extends Omit<CardData, 'team' | 'donation' | 'requestStatus'> {
  team: ConfirmTeamDialogData['team']
  pendingEvent?: 'cancelRequest' | 'putOnHoldRequest' | 'customerNotFound' | 'customerTakenCareOf'
}

export interface CardAllocatedAction {
  onCancelRequest: ConfirmTeamDialogAction['onConfirm']
  onCustomerTakenCareOf: () => void
  onCustomerNotFound: ConfirmTeamDialogAction['onConfirm']
  onPutOnHold: ConfirmTeamDialogAction['onConfirm']
}

export interface CardAllocatedProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    CardAllocateState,
    CardAllocatedData,
    CardAllocatedTimeFormat,
    CardAllocatedAction {
  labels: CardAllocatedLabels
  options: CardAllocatedOptions
}

export const CardAllocated = React.forwardRef<HTMLDivElement, CardAllocatedProps>(
  (
    {
      state,
      size,
      request,
      status,
      team: { usingMobileApp, ...team },
      locationLabel,
      pendingEvent,
      labels,
      options,
      highlighted,
      timeAgoFormatter,
      onCancelRequest,
      onCustomerTakenCareOf,
      onCustomerNotFound,
      onPutOnHold,
      ...props
    },
    ref
  ) => {
    const cellRef = createRef<HTMLDivElement>()
    useImperativeHandle(ref, () => cellRef.current!, [cellRef])
    useEffect(() => {
      if (highlighted) {
        cellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, [state, cellRef, highlighted])
    return (
      <Card
        className={highlighted ? 'animate-pulse-gray' : ''}
        state={state}
        size={size}
        labels={labels}
        request={request}
        requestStatus="allocated"
        status={status}
        team={team}
        locationLabel={locationLabel}
        timeAgoFormatter={timeAgoFormatter}
        ref={ref}
        {...props}>
        {state === 'active' && (
          <>
            <div className="flex h-full gap-2.5">
              <ConfirmTeamDialog
                variant="critical"
                labels={labels.cancelConfirmDialog}
                options={options}
                team={{ usingMobileApp, ...team }}
                onConfirm={onCancelRequest}
                working={pendingEvent === 'cancelRequest'}
                disabled={!!pendingEvent && pendingEvent !== 'cancelRequest'}
              />
              <ConfirmTeamDialog
                labels={labels.putOnHoldConfirmDialog}
                options={options}
                team={{ usingMobileApp, ...team }}
                onConfirm={onPutOnHold}
                working={pendingEvent === 'putOnHoldRequest'}
                disabled={!!pendingEvent && pendingEvent !== 'putOnHoldRequest'}
              />
            </div>
            <div className="flex h-full gap-2.5">
              <ConfirmTeamDialog
                labels={labels.customerNotFoundConfirmDialog}
                options={options}
                team={{ usingMobileApp, ...team }}
                onConfirm={onCustomerNotFound}
                working={pendingEvent === 'customerNotFound'}
                disabled={!!pendingEvent && pendingEvent !== 'customerNotFound'}
              />
              <ConfirmDialog
                labels={labels.customerTakenCareOfConfirmDialog}
                onConfirm={onCustomerTakenCareOf}
                working={pendingEvent === 'customerTakenCareOf'}
                disabled={!!pendingEvent && pendingEvent !== 'customerTakenCareOf'}
              />
            </div>
          </>
        )}
      </Card>
    )
  }
)
